/**
 * Token Manager Utility
 * Handles getting access/refresh tokens from Redux store (primary) with fallback to persist:userdb.
 */

import { store } from "../../store";
import { RootState } from "../../reducers";
import { getAuthDataFromPersistUser, clearAuthData } from "./authStorage";

/**
 * Gets access token from Redux store
 * @returns Access token string or undefined
 */
export function getAccessTokenFromStore(): string | undefined {
  const state = store.getState() as RootState;
  return state.authDetails?.auth?.accessToken;
}

/**
 * Gets refresh token from Redux store
 * @returns Refresh token string or undefined
 */
export function getRefreshTokenFromStore(): string | undefined {
  const state = store.getState() as RootState;
  return state.authDetails?.auth?.refreshToken;
}

/**
 * Gets access token from Redux store (primary source)
 * Falls back to persist:userdb only for initialization
 * @returns Access token string or undefined
 */
export function getAccessToken(): string | undefined {
  // Primary: Get from Redux store (this app's source of truth)
  const storeToken = getAccessTokenFromStore();
  if (storeToken) {
    return storeToken;
  }

  // Fallback: Get from persist:userdb (for initialization)
  const persistUserData = getAuthDataFromPersistUser();
  return persistUserData?.accessToken;
}

/**
 * Gets refresh token from Redux store (primary source)
 * Falls back to persist:userdb only for initialization
 * @returns Refresh token string or undefined
 */
export function getRefreshToken(): string | undefined {
  // Primary: Get from Redux store (this app's source of truth)
  const storeToken = getRefreshTokenFromStore();
  if (storeToken) {
    return storeToken;
  }

  // Fallback: Get from persist:userdb (for initialization)
  const persistUserData = getAuthDataFromPersistUser();
  return persistUserData?.refreshToken;
}

/**
 * Clears all auth data from localStorage and sessionStorage (legacy keys only).
 * persist:userdb is managed by Redux persist; auth is cleared via store dispatch.
 */
export function clearAuthStorage(): void {
  try {
    clearAuthData();

    // Clear any legacy direct localStorage/sessionStorage tokens (shouldn't exist, but clean up)
    localStorage.removeItem("accessToken");
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("refreshToken");
    localStorage.removeItem("refreshToken");
    
    // Note: persist:userdb is managed by Redux persist, will be cleared when store is reset
    console.log("🟢 clearAuthStorage: Cleared all auth storage");
  } catch (error) {
    console.error("🔴 clearAuthStorage: Error clearing auth storage", error);
  }
}

